import React from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useTranslation } from 'react-i18next';
import { STEP_TARGETS } from './onboarding/EditorTourHighlighter';
import './HorizontalOnboarding.css';

const HorizontalOnboarding = ({ onOpenSignup, onViewPricing, onCreateProject, hasActiveStandardTrial, handleLogOut }) => {
  const { isOnboardingActive, currentFlow, currentStep, completeStep, skipOnboarding, steps } = useOnboarding();
  const { t } = useTranslation();
  const [mobileDock, setMobileDock] = React.useState('top');
  const [desktopDock, setDesktopDock] = React.useState('right');

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateDock = () => {
      if (currentFlow !== 'editorTour') {
        setMobileDock('top');
        setDesktopDock('right');
        return;
      }

      const selector = STEP_TARGETS[currentStep];
      if (!selector) {
        setMobileDock('top');
        setDesktopDock('right');
        return;
      }

      const target = document.querySelector(selector);
      if (!target) {
        setMobileDock('top');
        setDesktopDock('right');
        return;
      }

      const rect = target.getBoundingClientRect();
      if (window.innerWidth <= 768) {
        const viewportMidpoint = window.innerHeight / 2;
        setMobileDock(rect.top < viewportMidpoint ? 'bottom' : 'top');
        setDesktopDock('right');
        return;
      }

      const viewportMidpointX = window.innerWidth / 2;
      setDesktopDock(rect.left > viewportMidpointX ? 'left' : 'right');
      setMobileDock('top');
    };

    updateDock();
    window.addEventListener('resize', updateDock);
    window.addEventListener('scroll', updateDock, { passive: true });

    return () => {
      window.removeEventListener('resize', updateDock);
      window.removeEventListener('scroll', updateDock);
    };
  }, [currentFlow, currentStep]);

  if (!isOnboardingActive) {
    return null; // Don't show if onboarding is not active
  }

  const getCurrentStepIndex = () => {
    return steps.indexOf(currentStep);
  };

  const getStepCopy = () => {
    if (currentFlow === 'editorTour') {
      switch (currentStep) {
        case 'editor-tour-welcome':
          return {
            title: t('onboarding.editorTour.welcome.title', 'Editor tour'),
            description: t('onboarding.editorTour.welcome.description', 'This quick tour walks through the main controls available in the editor so new users can understand where each tool lives.'),
          };
        case 'editor-tour-save':
          return {
            title: t('onboarding.editorTour.save.title', 'Save and project actions'),
            description: t('onboarding.editorTour.save.description', 'Use this top bar area to save your current project before leaving the editor or after a set of changes you want to keep.'),
          };
        case 'editor-tour-tools':
          return {
            title: t('onboarding.editorTour.tools.title', 'Editor tools'),
            description: t('onboarding.editorTour.tools.description', 'Use the bottom editor menu to add items, colors, shapes, and actions. When a category is opened, the selection panel above it shows the available items and previews for that tool.'),
          };
        case 'editor-tour-zoom':
          return {
            title: t('onboarding.editorTour.zoom.title', 'Zoom controls'),
            description: t('onboarding.editorTour.zoom.description', 'Use these controls to zoom in, zoom out, reset the view, fit the canvas, or collapse the zoom widget when you want more room.'),
          };
        case 'editor-tour-video':
          return {
            title: t('onboarding.editorTour.video.title', 'Video tutorial'),
            description: t('onboarding.editorTour.video.description', 'This badge opens the editor walkthrough video. Keep it available for self-serve help, or collapse it to the small icon if it gets in the way.'),
          };
        case 'editor-tour-designer':
          return {
            title: t('onboarding.editorTour.designer.title', 'Virtual Designer'),
            description: t('onboarding.editorTour.designer.description', 'Launch the Virtual Designer here when you want AI help with ideas, automatic placements, or realistic facade generations.'),
          };
        default:
          return {
            title: t('onboarding.title', 'Onboarding'),
            description: '',
          };
      }
    }

    if (currentFlow === 'member') {
      switch (currentStep) {
        case 'project-selection':
          return {
            title: t('onboarding.member.projectSelection.title', 'Open one of your projects'),
            description: t('onboarding.member.projectSelection.description', 'Choose a saved project to load the editor and walk through the core design workflow again.'),
          };
        case 'editor-loaded':
          return {
            title: t('onboarding.member.editorLoaded.title', 'Project loaded'),
            description: t('onboarding.member.editorLoaded.description', 'Your project is now open. Launch the AI Designer to get help with colors, doors, and windows.'),
          };
        case 'ai-interaction':
          return {
            title: t('onboarding.member.aiInteraction.title', 'AI Designer ready'),
            description: t('onboarding.member.aiInteraction.description', 'Write your own prompt or pick one of the suggested requests to start the guided tour.'),
          };
        case 'show-mode-selected':
          return {
            title: t('onboarding.member.showModeSelected.title', 'Mode Montrez-moi sélectionné'),
            description: t('onboarding.member.showModeSelected.description', 'Très bien. Descendez maintenant au bas du Designer, gardez ou ajustez le message proposé, puis cliquez sur Envoyer pour lancer la recommandation.'),
          };
        case 'submit-request':
          return {
            title: t('onboarding.member.submitRequest.title', 'Send your request'),
            description: t('onboarding.member.submitRequest.description', 'Submit a request so the AI can analyze the facade and prepare recommendations.'),
          };
        case 'processing-request':
          return {
            title: t('onboarding.member.processingRequest.title', 'AI processing'),
            description: t('onboarding.member.processingRequest.description', 'The AI is analyzing the current facade and preparing a recommendation for your project.'),
          };
        case 'recommendations-ready':
          return {
            title: t('onboarding.member.recommendationsReady.title', 'Recommendations ready'),
            description: t('onboarding.member.recommendationsReady.description', 'Review the answer or click "Show Me" if you want the suggested changes applied directly to your project.'),
          };
        case 'editing-tools':
          return {
            title: t('onboarding.member.editingTools.title', 'Refine the result'),
            description: t('onboarding.member.editingTools.description', 'The suggested windows, doors, and colors are now on your facade. Keep editing manually to refine the result.'),
          };
        case 'ready-to-design':
          return {
            title: t('onboarding.member.readyToDesign.title', 'You are ready to continue'),
            description: t('onboarding.member.readyToDesign.description', 'Your tour is complete. Keep designing, save your project, or open another one whenever you want.'),
          };
        default:
          return {
            title: t('onboarding.title', 'Onboarding'),
            description: '',
          };
      }
    }

    if (currentFlow === 'memberNewProject') {
      switch (currentStep) {
        case 'create-project':
          return {
            title: t('onboarding.memberNewProject.createProject.title', 'Create your first project'),
            description: t('onboarding.memberNewProject.createProject.description', 'You do not have any saved projects yet. Click Start Project to open the project setup flow and create your first one.'),
          };
        case 'enter-project-name':
          return {
            title: t('onboarding.memberNewProject.enterProjectName.title', 'Enter a project name'),
            description: t('onboarding.memberNewProject.enterProjectName.description', 'Start by filling in the project name field so the new project can be saved with a clear label.'),
          };
        case 'select-facade-image':
          return {
            title: t('onboarding.memberNewProject.selectFacadeImage.title', 'Select the facade image'),
            description: t('onboarding.memberNewProject.selectFacadeImage.description', 'Choose the image of the facade from your device or use the upload area to attach it to the project.'),
          };
        case 'upload-project':
          return {
            title: t('onboarding.memberNewProject.uploadProject.title', 'Create the project'),
            description: t('onboarding.memberNewProject.uploadProject.description', 'When the project name and image are ready, click Upload to finalize the creation of the project and open it in the editor.'),
          };
        case 'editor-loaded':
          return {
            title: t('onboarding.memberNewProject.editorLoaded.title', 'Project loaded'),
            description: t('onboarding.memberNewProject.editorLoaded.description', 'Your first project is now open. Launch the AI Designer to get help with colors, doors, and windows.'),
          };
        case 'ai-interaction':
          return {
            title: t('onboarding.memberNewProject.aiInteraction.title', 'AI Designer ready'),
            description: t('onboarding.memberNewProject.aiInteraction.description', 'Write your own prompt or pick one of the suggested requests to start the guided tour.'),
          };
        case 'show-mode-selected':
          return {
            title: t('onboarding.memberNewProject.showModeSelected.title', 'Mode Montrez-moi sélectionné'),
            description: t('onboarding.memberNewProject.showModeSelected.description', 'Très bien. Descendez maintenant au bas du Designer, gardez ou ajustez le message proposé, puis cliquez sur Envoyer pour lancer la recommandation.'),
          };
        case 'submit-request':
          return {
            title: t('onboarding.memberNewProject.submitRequest.title', 'Send your request'),
            description: t('onboarding.memberNewProject.submitRequest.description', 'Submit a request so the AI can analyze the facade and prepare recommendations.'),
          };
        case 'processing-request':
          return {
            title: t('onboarding.memberNewProject.processingRequest.title', 'AI processing'),
            description: t('onboarding.memberNewProject.processingRequest.description', 'The AI is analyzing the current facade and preparing a recommendation for your project.'),
          };
        case 'recommendations-ready':
          return {
            title: t('onboarding.memberNewProject.recommendationsReady.title', 'Recommendations ready'),
            description: t('onboarding.memberNewProject.recommendationsReady.description', 'Review the answer or click "Show Me" if you want the suggested changes applied directly to your project.'),
          };
        case 'editing-tools':
          return {
            title: t('onboarding.memberNewProject.editingTools.title', 'Refine the result'),
            description: t('onboarding.memberNewProject.editingTools.description', 'The suggested windows, doors, and colors are now on your facade. Keep editing manually to refine the result.'),
          };
        case 'ready-to-design':
          return {
            title: t('onboarding.memberNewProject.readyToDesign.title', 'You are ready to continue'),
            description: t('onboarding.memberNewProject.readyToDesign.description', 'Your first project is ready. Keep designing, save your work, or create another project whenever you want.'),
          };
        default:
          return {
            title: t('onboarding.title', 'Onboarding'),
            description: '',
          };
      }
    }

    if (currentFlow === 'signup') {
      switch (currentStep) {
        case 'purchase-plan':
          return hasActiveStandardTrial ? {
            title: t('onboarding.trialProject.title', { defaultValue: 'Your free trial is ready' }),
            description: t('onboarding.trialProject.description', { defaultValue: 'Create your first project with your own house photo. No payment is required during your 7-day trial.' }),
          } : {
            title: t('onboarding.purchasePlan.title', { defaultValue: 'Choose how to get started' }),
            description: t('onboarding.purchasePlan.description', { defaultValue: 'Start the free 7-day trial on your own house or choose a paid package when you are ready.' }),
          };
        default:
          return {
            title: t('onboarding.title', 'Onboarding'),
            description: '',
          };
      }
    }

    switch (currentStep) {
      case 'project-selection':
        return {
          title: t('onboarding.projectSelection.title', 'Choose a demo project'),
          description: t('onboarding.projectSelection.description', 'Open one of the guest projects to load the editor and try the workflow without creating an account.'),
        };
      case 'editor-loaded':
        return {
          title: t('onboarding.editorLoaded.title', 'Project loaded'),
          description: t('onboarding.editorLoaded.description', 'Your project is now loaded. Open the AI Designer to get help with colors, doors, and windows.'),
        };
      case 'ai-interaction':
        return {
          title: t('onboarding.aiInteraction.title', 'AI Designer ready'),
          description: t('onboarding.aiInteraction.description', 'Write your own request or use one of the suggested prompts to start the conversation.'),
        };
      case 'show-mode-selected':
        return {
          title: t('onboarding.showModeSelected.title', 'Show Me selected'),
          description: t('onboarding.showModeSelected.description', 'Now scroll to the bottom of the Designer, keep or adjust the suggested message, and click Send to start the recommendation.'),
        };
      case 'submit-request':
        return {
          title: t('onboarding.submitRequest.title', 'Send your request'),
          description: t('onboarding.submitRequest.description', 'Submit a request so the AI can analyze the facade and prepare recommendations.'),
        };
      case 'processing-request':
        return {
          title: t('onboarding.processingRequest.title', 'AI processing'),
          description: t('onboarding.processingRequest.description', 'The AI is analyzing the image and preparing suggested changes for your facade.'),
        };
      case 'recommendations-ready':
        return {
          title: t('onboarding.recommendationsReady.title', 'Recommendations ready'),
          description: t('onboarding.recommendationsReady.description', 'Review the answer or click "Show Me" if you want the suggested changes applied automatically.'),
        };
      case 'editing-tools':
        return {
          title: t('onboarding.editingTools.title', 'Review the changes'),
          description: t('onboarding.editingTools.description', 'The suggested windows, doors, and colors are now on the facade. You can keep refining them manually.'),
        };
      case 'save-limitations':
        return {
          title: t('onboarding.saveLimitations.title', 'Guest mode ends here'),
          description: t('onboarding.saveLimitations.description', 'Guest mode is only for exploration. Create an account to start the 7-day Standard trial on your own house, or choose a paid plan if you are ready to continue.'),
        };
      default:
        return {
          title: t('onboarding.title', 'Onboarding'),
          description: '',
        };
    }
  };

  const stepCopy = getStepCopy();

  return (
    <div className={`horizontal-onboarding ${currentFlow === 'editorTour' ? `editor-tour-card editor-tour-card-${mobileDock} editor-tour-card-desktop-${desktopDock}` : ''}`}>
      <div className="horizontal-onboarding-content">
        <div className="horizontal-onboarding-progress">
          <span className="horizontal-onboarding-step">
            {t('onboarding.progress.step', 'Step {{current}} of {{total}}', { current: getCurrentStepIndex() + 1, total: steps.length })}
          </span>
          <div className="horizontal-onboarding-progress-bar">
            <div 
              className="horizontal-onboarding-progress-fill"
              style={{ width: `${((getCurrentStepIndex() + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
        
        <div className="horizontal-onboarding-text">
          <h4 className="horizontal-onboarding-title">{stepCopy.title}</h4>
          <p className="horizontal-onboarding-description">{stepCopy.description}</p>
        </div>
        
        <div className="horizontal-onboarding-actions">
          <button 
            className="horizontal-onboarding-skip btn btn-sm btn-outline-secondary"
            onClick={skipOnboarding}
            title={t('onboarding.buttons.skipTitle', 'Skip onboarding')}
          >
            {t('onboarding.buttons.skip', 'Skip')}
          </button>
          {currentStep === 'purchase-plan' && (
            <button
              className="horizontal-onboarding-next btn btn-sm btn-primary"
              onClick={() => {
                completeStep(currentStep);
                if (hasActiveStandardTrial && onCreateProject) {
                  onCreateProject();
                } else if (onViewPricing) {
                  onViewPricing();
                }
              }}
            >
              {hasActiveStandardTrial
                ? t('onboarding.trialProject.action', { defaultValue: 'Create my project' })
                : t('onboarding.buttons.viewPlans', { defaultValue: 'View plans' })}
            </button>
          )}
          {currentFlow === 'editorTour' && (
            <button
              className="horizontal-onboarding-next btn btn-sm btn-primary"
              onClick={() => completeStep(currentStep)}
            >
              {t('onboarding.buttons.gotIt', 'Got it!')}
            </button>
          )}
          {currentStep === 'ready-to-design' && (
            <button
              className="horizontal-onboarding-next btn btn-sm btn-primary"
              onClick={() => completeStep(currentStep)}
            >
              {t('onboarding.buttons.continueDesigning', 'Continue designing')}
            </button>
          )}
          {currentStep === 'create-project' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.memberNewProject.createProject.action', 'Cliquez sur Nouveau pour créer votre premier projet')}</span>
            </div>
          )}
          {(currentStep === 'enter-project-name' || currentStep === 'select-facade-image' || currentStep === 'upload-project') && (
            <button
              className="horizontal-onboarding-next btn btn-sm btn-primary"
              onClick={() => completeStep(currentStep)}
            >
              {t('onboarding.buttons.gotIt', 'Got it!')}
            </button>
          )}
          {currentStep === 'project-selection' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.projectSelection.action', '🏠 Click the project image to open it')}</span>
            </div>
          )}
          {currentStep === 'editor-loaded' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.waiting.clickAiDesigner', '👆 Click the AI Designer button')}</span>
            </div>
          )}
          {currentStep === 'ai-interaction' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.waiting.selectShowMeMode', 'Choisissez le mode "Montrez-moi" dans le Designer')}</span>
            </div>
          )}
          {currentStep === 'show-mode-selected' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.waiting.scrollAndSend', 'Faites défiler jusqu’en bas du Designer puis cliquez sur "Envoyer"')}</span>
            </div>
          )}
          {currentStep === 'submit-request' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.waiting.sendingRequest', 'Votre demande est en cours d’envoi au Designer')}</span>
            </div>
          )}
          {currentStep === 'processing-request' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.waiting.aiAnalyzing', '⏳ AI is analyzing your facade...')}</span>
            </div>
          )}
          {currentStep === 'recommendations-ready' && (
            <div className="horizontal-onboarding-waiting">
              <span>{t('onboarding.waiting.clickShowMe', '🎨 Want an automatic preview? Click "Show Me"')}</span>
            </div>
          )}
          {currentStep === 'editing-tools' && (
            <button 
              className="horizontal-onboarding-next btn btn-sm btn-primary"
              onClick={() => completeStep(currentStep)}
            >
              {t('onboarding.buttons.gotIt', 'Got it!')}
            </button>
          )}
          {currentStep === 'save-limitations' && (
            <div className="horizontal-onboarding-cta">
              <button 
                className="horizontal-onboarding-register btn btn-sm btn-success me-2"
                onClick={async () => {
                  if (onOpenSignup && handleLogOut) {
                    // First close the onboarding
                    skipOnboarding();
                    // Then logout the guest user
                    await handleLogOut();
                    // Finally open the signup modal
                    onOpenSignup();
                  } else {
                    alert(t('onboarding.alerts.registrationComingSoon', 'Registration feature coming soon!'));
                  }
                }}
                title={t('onboarding.buttons.registerTitle', 'Register to save your designs')}
              >
                {t('onboarding.buttons.registerNow', 'Sign up')}
              </button>
              <button
                className="horizontal-onboarding-next btn btn-sm btn-primary me-2"
                onClick={() => {
                  skipOnboarding();
                  if (onViewPricing) {
                    onViewPricing();
                  }
                }}
              >
                {t('onboarding.buttons.viewPlans', 'View plans')}
              </button>
              <button 
                className="horizontal-onboarding-next btn btn-sm btn-outline-primary"
                onClick={() => completeStep(currentStep)}
              >
                {t('onboarding.buttons.continueAsGuest', 'Continue as Guest')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HorizontalOnboarding;
